import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Github, Linkedin, Twitter, Instagram, Zap } from 'lucide-react';
import { useTheme } from '../../contexts/Themecontext';

const Footer = () => {
  const { theme } = useTheme();
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    Company: [
      { label: 'About Origin', path: '/about' },
      { label: 'Innovation HorizonX', path: '/innovation' },
      { label: 'FutureGrid Roadmap', path: '/roadmap' },
      { label: 'TalentForge Careers', path: '/careers' },
    ],
    Products: [
      { label: 'CoreStack', path: '/products' },
      { label: 'QuantumEdge Solutions', path: '/solutions' },
      { label: 'QubitWorks Platform', path: '/software' },
    ],
    Technology: [
      { label: 'NanoCore Semiconductor', path: '/semiconductor' },
      { label: 'SuperFlux Superconductor', path: '/superconductor' },
      { label: 'QuantumLab Research', path: '/research' },
    ],
    Resources: [
      { label: 'InsightX Blog', path: '/blog' },
      { label: 'HelpDesk Support', path: '/support' },
      { label: 'DevSphere Documentation', path: '/documentation' },
    ],
  };

  const getThemeClasses = () => {
    if (theme === 'light') {
      return {
        bg: 'bg-gray-50',
        border: 'border-gray-200',
        text: 'text-gray-900',
        textMuted: 'text-gray-600',
        hover: 'hover:text-blue-600',
        socialBg: 'bg-white border-gray-200',
        socialHover: 'hover:bg-blue-50 hover:border-blue-300',
      };
    }
    if (theme === 'dark') {
      return {
        bg: 'bg-slate-900',
        border: 'border-slate-700',
        text: 'text-white',
        textMuted: 'text-gray-400',
        hover: 'hover:text-cyan-400',
        socialBg: 'bg-slate-800 border-slate-700',
        socialHover: 'hover:bg-slate-700 hover:border-cyan-500',
      };
    }
    return {
      bg: 'bg-gradient-to-b from-slate-950 via-indigo-950 to-purple-950',
      border: 'border-purple-500/30',
      text: 'text-white',
      textMuted: 'text-gray-300',
      hover: 'hover:text-purple-300',
      socialBg: 'bg-purple-500/10 border-purple-500/30',
      socialHover: 'hover:bg-purple-500/20 hover:border-purple-400',
    };
  };

  const classes = getThemeClasses();

  return (
    <footer className={`relative ${classes.bg} border-t ${classes.border} mt-20`}>
      {/* Wave Animation */}
      <div className="absolute top-0 left-0 w-full overflow-hidden leading-none">
        <svg className="relative block w-full h-12 sm:h-16 md:h-20" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path
            d="M0,50 C300,100 600,0 900,50 L900,120 L0,120 Z"
            className={theme === 'quantum' ? 'fill-purple-500/10' : theme === 'dark' ? 'fill-slate-800/50' : 'fill-gray-100'}
            opacity="0.5"
          />
          <path
            d="M0,70 C300,20 600,100 900,70 L900,120 L0,120 Z"
            className={theme === 'quantum' ? 'fill-purple-500/20' : theme === 'dark' ? 'fill-slate-800' : 'fill-gray-200'}
          />
        </svg>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 pb-8">
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-6 mb-12">
          {/* Brand Column */}
          <div className="sm:col-span-2 lg:col-span-2">
            <Link to="/" className="inline-flex items-center gap-2 sm:gap-3 mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-cyan-400 via-purple-500 to-pink-500 flex items-center justify-center">
                <Zap className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              <div className="flex items-center gap-1">
                <span
                  className={`text-2xl sm:text-3xl font-bold ${
                    theme === 'quantum'
                      ? 'bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent'
                      : theme === 'dark'
                      ? 'text-cyan-400'
                      : 'text-blue-600'
                  }`}
                >
                  GLY
                </span>
                <span
                  className={`text-2xl sm:text-3xl font-bold ${
                    theme === 'quantum'
                      ? 'bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent'
                      : theme === 'dark'
                      ? 'text-purple-400'
                      : 'text-purple-600'
                  }`}
                >
                  QIX
                </span>
              </div>
            </Link>
            <p className={`${classes.textMuted} text-sm sm:text-base mb-6 max-w-md`}>
              Advancing the future of quantum computing, semiconductor engineering, and superconducting systems.
            </p>
            {/* Social Icons */}
            <div className="flex flex-wrap gap-3">
              <a
                href="https://x.com/glyqix"
                target="_blank"
                rel="noreferrer"
                className={`p-2.5 sm:p-3 rounded-lg border transition-all duration-200 ${classes.socialBg} ${classes.socialHover}`}
                aria-label="Twitter"
              >
                <Twitter className="w-4 h-4 sm:w-5 sm:h-5" />
              </a>
              <a
                href="https://www.linkedin.com/company/glyqix"
                target="_blank"
                rel="noreferrer"
                className={`p-2.5 sm:p-3 rounded-lg border transition-all duration-200 ${classes.socialBg} ${classes.socialHover}`}
                aria-label="LinkedIn"
              >
                <Linkedin className="w-4 h-4 sm:w-5 sm:h-5" />
              </a>
              <a
                href="https://instagram.com/glyqix"
                target="_blank"
                rel="noreferrer"
                className={`p-2.5 sm:p-3 rounded-lg border transition-all duration-200 ${classes.socialBg} ${classes.socialHover}`}
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4 sm:w-5 sm:h-5" />
              </a>
              <a
                href="https://github.com"
                target="_blank"
                rel="noreferrer"
                className={`p-2.5 sm:p-3 rounded-lg border transition-all duration-200 ${classes.socialBg} ${classes.socialHover}`}
                aria-label="GitHub"
              >
                <Github className="w-4 h-4 sm:w-5 sm:h-5" />
              </a>
            </div>
          </div>

          {/* Link Columns */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category} className="min-w-0">
              <h4
                className={`${classes.text} text-sm sm:text-base font-bold mb-4 ${
                  theme === 'quantum' ? 'bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent' : ''
                }`}
              >
                {category}
              </h4>
              <ul className="space-y-2.5 sm:space-y-3">
                {links.map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.path}
                      className={`${classes.textMuted} ${classes.hover} text-sm sm:text-base transition-colors duration-200 inline-block`}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Contact Bar */}
        <div
          className={`py-6 sm:py-8 border-t border-b ${classes.border} mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8`}
        >
          <div className={`flex items-center gap-3 ${classes.textMuted}`}>
            <Mail className="w-5 h-5 flex-shrink-0" />
            <a href="mailto:info@glyqix.com" className={`text-sm sm:text-base ${classes.hover} transition-colors duration-200`}>
              info@glyqix.com
            </a>
          </div>
          <div className={`flex items-center gap-3 ${classes.textMuted}`}>
            <MapPin className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm sm:text-base">Innovation District, Tech City</span>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className={`${classes.textMuted} text-xs sm:text-sm text-center sm:text-left`}>
            © {currentYear} GLYQIX. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center sm:justify-end gap-4 sm:gap-6">
            <Link to="/privacy" className={`${classes.textMuted} ${classes.hover} text-xs sm:text-sm transition-colors duration-200`}>
              Privacy Policy
            </Link>
            <Link to="/terms" className={`${classes.textMuted} ${classes.hover} text-xs sm:text-sm transition-colors duration-200`}>
              Terms of Service
            </Link>
          </div>
        </div>
      </div>

      {/* Quantum Particles for Quantum Theme */}
      {theme === 'quantum' && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-30">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-purple-500 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            />
          ))}
        </div>
      )}
    </footer>
  );
};

export default Footer;